import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { PageHeader, SectionCard } from "@/components/common/Primitives";
import { KpiCard } from "@/components/common/KpiCard";
import { fmtEUR } from "@/lib/format";
import { CATEGORY_LABELS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip,
} from "recharts";
import { Download, Users, FileText, Euro, RotateCw } from "lucide-react";

const COLORS = ["hsl(var(--chart-1))", "hsl(var(--chart-2))", "hsl(var(--chart-3))", "hsl(var(--chart-4))", "hsl(var(--chart-5))"];

export default function Reports() {
  const { user } = useAuth();
  const { data } = useQuery({ queryKey: ["dashboard-reports"], queryFn: async () => (await api.get("/dashboard")).data });
  const k = data?.kpis || {};
  const byModule = data?.by_module || {};
  const pie = Object.entries(byModule).map(([key, value]) => ({ name: key === "telekom" ? "Telekommunikation" : key === "energie" ? "Energie" : key, value }));

  const exportCsv = async (entity) => {
    try {
      const res = await api.get(`/export/${entity}?format=csv`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${entity}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Export gestartet");
    } catch { toast.error("Export fehlgeschlagen"); }
  };

  return (
    <div>
      <PageHeader title="Berichte" description="Kennzahlen aus echten Mandantendaten"
        actions={can(user?.role, "export") && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => exportCsv("customers")} data-testid="export-customers-button"><Download className="mr-1.5 h-4 w-4" />Kunden CSV</Button>
            <Button variant="outline" size="sm" onClick={() => exportCsv("contracts")} data-testid="export-contracts-button"><Download className="mr-1.5 h-4 w-4" />Verträge CSV</Button>
          </div>
        )} />

      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard testId="report-kpi-customers" label="Kunden gesamt" value={k.customers} icon={Users} />
        <KpiCard testId="report-kpi-contracts" label="Aktive Verträge" value={k.active_contracts} icon={FileText} />
        <KpiCard testId="report-kpi-revenue" label="Mtl. Umsatz" value={fmtEUR(k.monthly_revenue)} icon={Euro} tone="accent" />
        <KpiCard testId="report-kpi-renewals" label="Verlängerungen (anstehend)" value={k.upcoming_renewals} icon={RotateCw} tone="info" />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <SectionCard title="Aktive Verträge nach Branche">
          <div className="h-64">
            {pie.length === 0 ? <p className="py-20 text-center text-sm text-muted-foreground">Keine Daten</p> : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pie} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} innerRadius={50} paddingAngle={2}>
                    {pie.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </SectionCard>

        <SectionCard title="Verlängerungs-Ergebnisse">
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-between"><span className="text-sm text-muted-foreground">Verlängert</span><span className="text-2xl font-semibold text-emerald-600">{k.renewed}</span></div>
            <div className="flex items-center justify-between"><span className="text-sm text-muted-foreground">Verloren</span><span className="text-2xl font-semibold text-destructive">{k.lost}</span></div>
            <div className="flex items-center justify-between border-t border-border pt-4"><span className="text-sm text-muted-foreground">Offene Aufgaben</span><span className="text-2xl font-semibold">{k.open_tasks}</span></div>
            <div className="flex items-center justify-between"><span className="text-sm text-muted-foreground">Ungeprüfte Dokumente</span><span className="text-2xl font-semibold text-amber-600">{k.unmatched_documents}</span></div>
          </div>
        </SectionCard>
      </div>
    </div>
  );
}
